import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  FormControl,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from '@unform/core';
import { isEqual, isArray } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Spinner } from '~/components';
import { t, canURL } from '~/helpers';
import api from '~/services/api';

import { Research } from './Research';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    height: 230,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
  wraper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'center',
  },
  span: {
    fontSize: 12,
    fontFamily: 'Poppins',
    fontWeight: 400,
    lineHeight: 1.43,
  },
  action: {
    marginTop: 'auto',
    marginBottom: 'auto',
    selfAlign: 'center',
  },
}));

function CheckboxGroup({
  name,
  label,
  getValue,
  url,
  title,
  unique,
  disabled,
  query,
  search,
  concat,
  onLoadingChange,
  onLoadingError,
}) {
  const inputRef = useRef(null);
  const classes = useStyles();
  const permission = isEqual(canURL(), { read: true });

  const { fieldName, defaultValue, registerField } = useField(name);

  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState(defaultValue || []);
  const [searchLabel, setSearchLabel] = useState('');
  const [filterSelecionados, setFilterSelecionados] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleToggle(value) {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    inputRef.current.value = newChecked;
    getValue(newChecked);
  }

  async function handleGetData() {
    try {
      onLoadingChange(true);
      setLoading(true);
      const { data } = await api.get(url, {
        params: { pagesize: 99999, pesquisalista: true, ...query },
        paramsSerializer: (q) => qs.stringify(q),
      });

      setItems(data?.items ?? data);
    } catch (err) {
      onLoadingError(err);
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      setLoading(false);
      onLoadingChange(false);
    }
  }

  async function handleSelectAll() {
    const newItems = items.map((value) => value[unique]);

    if (items.length === checked.length) {
      setChecked([]);
      inputRef.current.value = [];
      getValue([]);
    } else {
      setChecked(newItems);
      inputRef.current.value = newItems;
      getValue(newItems);
    }
  }

  function handleSwitch() {
    setFilterSelecionados((prevValue) => !prevValue);
  }

  function switchFilter(value) {
    if (filterSelecionados) {
      return checked.includes(value[unique]);
    }

    return value;
  }

  function searchFilter(value) {
    return (
      value[label]
        .toLocaleLowerCase()
        .indexOf(searchLabel.toLocaleLowerCase()) !== -1
    );
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    handleGetData();
  }, []);

  useEffect(() => {
    if (typeof checked === 'string') {
      const arrayValue = checked
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => !isNaN(v));

      setChecked(arrayValue);
    }
  }, [checked]);

  return (
    <FormControl style={{ width: '100%' }}>
      <input ref={inputRef} type="hidden" />
      <Card>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Checkbox
              onClick={() => handleSelectAll()}
              checked={checked.length === items.length && checked.length !== 0}
              indeterminate={
                checked.length !== items.length && checked.length !== 0
              }
              disabled={permission || items.length === 0}
              inputProps={{ 'aria-label': 'itens selecionados' }}
            />
          }
          action={
            <div className={classes.wraper}>
              <span className={classes.span}>Selecionados</span>
              <Switch checked={filterSelecionados} onChange={handleSwitch} />
            </div>
          }
          title={title}
          subheader={`${checked.length}/${items.length} selecionados`}
          classes={{
            action: classes.action,
          }}
        />

        {search && <Research onSearch={setSearchLabel} />}

        <Divider />

        <List className={classes.list} dense component="div" role="list">
          <Spinner loading={loading} />
          {items
            .filter((value) => switchFilter(value))
            .filter((value) => searchFilter(value))
            .map((value) => {
              const labelId = `transfer-list-all-item-${value[unique]}-label`;

              return (
                <ListItem
                  key={value[unique]}
                  role="listitem"
                  button
                  onClick={() =>
                    permission ? undefined : handleToggle(value[unique])
                  }
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value[unique]) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                      disabled={permission || disabled}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      concat
                        ? `${value[label]} - ${value[concat]}`
                        : value[label]
                    }
                  />
                </ListItem>
              );
            })}
          <ListItem />
        </List>
      </Card>
    </FormControl>
  );
}

CheckboxGroup.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  unique: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  url: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  query: PropTypes.objectOf(PropTypes.any),
  search: PropTypes.bool,
  concat: PropTypes.string,
  onLoadingChange: PropTypes.func,
  onLoadingError: PropTypes.func,
};

CheckboxGroup.defaultProps = {
  getValue: () => {},
  disabled: false,
  search: false,
  concat: null,
  query: {},
  onLoadingChange: () => {},
  onLoadingError: () => {},
};

export default React.memo(CheckboxGroup);
